/**
 * Represents a business unit permission scope.
 * Can be 'UNKNOWN' for unrecognized units or a specific business unit string, e.g. 'DI_FA'.
 */
export type PermissionBusinessUnit = 'UNKNOWN' | string;

export enum Role {
  /** User with consumer-level access */
  Consumer = 'Consumer',
  /** User with maintainer-level access */
  Maintainer = 'Maintainer',
  /** User with administrative privileges */
  Admin = 'Admin',
  /** User with developer access (not used in the current implementation, will be deprecated from DirX) */
  Developer = 'Developer',
  /** User with unknown or unassigned role */
  Unknown = 'Unknown',
}

/**
 * Evaluates a role string and returns the corresponding Role enum value.
 * @param {string} role - The role string to evaluate (e.g., "CONSUMER-DI_CS", "TEST-ADMIN").
 * @returns {Role} The corresponding Role enum value based on role content.
 * @example
 * const role = evaluateRole('CONSUMER-DI_CS'); // Returns Role.Consumer
 * const adminRole = evaluateRole('TEST-ADMIN'); // Returns Role.Admin
 * const unknownRole = evaluateRole('CUSTOM_ROLE'); // Returns Role.Unknown
 */
export const evaluateRole = (role: string): Role => {
  if (role.includes('DEVADMIN')) {
    return Role.Unknown;
  }
  if (role.includes('CONSUMER')) {
    return Role.Consumer;
  }
  if (role.includes('ADMIN')) {
    return Role.Admin;
  }
  if (role.includes('MAINTAINER')) {
    return Role.Maintainer;
  }
  if (role.includes('DEVELOPER')) {
    return Role.Developer;
  }
  return Role.Unknown;
};

/**
 * Extracts the business unit identifier from a role string.
 * NOTE: Business unit names contains underscore in the role string but we want to handle them with spaces because they
 * have to match with business units coming from the UPM.
 * @param {string} role - The role string to parse (e.g., "CONSUMER-DI_CS", "MAINTAINER-SI_E").
 * @returns {string} The business unit identifier or 'UNKNOWN' if not found.
 * @example
 * const bu = evaluateBusinessUnit('CONSUMER-DI_CS'); // Returns 'DI CS'
 * const bu2 = evaluateBusinessUnit('TEST-MAINTAINER-SI_E'); // Returns 'SI E'
 * const unknown = evaluateBusinessUnit('ADMIN'); // Returns 'UNKNOWN'
 */
export const evaluateBusinessUnit = (role: string): PermissionBusinessUnit => {
  const businessUnits = role
    .split('-')
    .filter((item) => item !== 'CONSUMER' && item !== 'MAINTAINER' && item !== 'TEST' && item !== 'ADMIN');
  return businessUnits[0].replace(/_/g, ' ') || 'UNKNOWN';
};

/**
 * Converts an array of role strings to unique Role enum values.
 * @param {string[]} roles - Array of role strings to convert.
 * @returns {Role[]} Array of unique Role enum values.
 * @example
 * const roles = getRoles(['CONSUMER-DI_CS', 'ADMIN', 'CONSUMER-SI_E']);
 * // Returns [Role.Consumer, Role.Admin]
 */
export const mapRoles = (roles: string[]): Role[] => {
  const allRoles = roles.map((role) => evaluateRole(role));
  const uniqueRoles = [...new Set(allRoles)];
  return uniqueRoles;
};

/**
 * Used to return all of the business units that the user has access to.
 * Consumer and maintainer roles return their specific business units.
 * @param {string[]} roles - Array of role strings to process.
 * @param {'maintainer' | 'consumer'} type - The type of roles to filter for.
 * @returns {PermissionBusinessUnit[]} Array of unique business unit identifiers.
 * @example
 * const units = getBusinessUnitsFromRoles(['ADMIN', 'CONSUMER-DI_CS', 'MAINTAINER-SI_E'], 'consumer');
 * // Returns ['DI CS']
 */
export const getBusinessUnitsFromRoles = (
  roles: string[],
  type: 'maintainer' | 'consumer',
): PermissionBusinessUnit[] => {
  const businessUnits: PermissionBusinessUnit[] = [];
  roles.forEach((role) => {
    if (role.includes('ADMIN') && type === 'maintainer') {
      // Currently ADMIN role have access to all business units for maintainers
      businessUnits.push('ALL');
    }
    if (type === 'consumer' && role.includes('CONSUMER')) {
      businessUnits.push(evaluateBusinessUnit(role));
    }
    if (type === 'maintainer' && role.includes('MAINTAINER')) {
      businessUnits.push(evaluateBusinessUnit(role));
    }
  });
  return [...new Set(businessUnits)];
};
